import { useState, useEffect } from 'react';
import { Check, ChevronDown, X, Tag, Plus } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface TagType {
  id: string;
  name: string;
  color?: string;
}

interface TagFilterDropdownProps {
  selectedTags: string[];
  onTagsChange: (tags: string[]) => void;
  availableTags: TagType[];
  organizationId: string;
  onTagCreated?: () => void;
}

export function TagFilterDropdown({
  selectedTags,
  onTagsChange,
  availableTags,
  organizationId,
  onTagCreated,
}: TagFilterDropdownProps) {
  const [open, setOpen] = useState(false);
  const [tempSelectedTags, setTempSelectedTags] = useState<string[]>(selectedTags);
  const [query, setQuery] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [options, setOptions] = useState<TagType[]>(availableTags);
  const [isSearching, setIsSearching] = useState(false);

  const normalize = (s: string) => s.trim().replace(/\s+/g, ' ').toLowerCase();
  const normalizedQuery = normalize(query);

  // Debounced search
  useEffect(() => {
    if (!open || !organizationId) return;
    
    const searchTags = async () => {
      setIsSearching(true);
      try {
        let tagQuery = supabase
          .from('tags')
          .select('id, name, color')
          .eq('organization_id', organizationId)
          .order('name');
        
        // Add search filter if query exists
        if (normalizedQuery) {
          tagQuery = tagQuery.ilike('name', `%${normalizedQuery}%`);
        }
        
        const { data } = await tagQuery.limit(100);
        if (data) setOptions(data);
      } finally {
        setIsSearching(false);
      }
    };

    const timeoutId = setTimeout(searchTags, 300);
    return () => clearTimeout(timeoutId);
  }, [open, organizationId, normalizedQuery]);

  const handleOpenChange = (isOpen: boolean) => {
    setOpen(isOpen);
    if (isOpen) {
      // Reset temp selection and query when opening
      setTempSelectedTags(selectedTags);
      setQuery('');
    }
  };

  const toggleTag = (tagId: string) => {
    if (tempSelectedTags.includes(tagId)) {
      setTempSelectedTags(tempSelectedTags.filter(id => id !== tagId));
    } else {
      setTempSelectedTags([...tempSelectedTags, tagId]);
    }
  };

  const handleApply = () => {
    onTagsChange(tempSelectedTags);
    setOpen(false);
  };

  const createNewTag = async () => {
    const trimmedQuery = query.trim();
    if (!trimmedQuery || !organizationId) return;

    setIsCreating(true);
    try {
      const { data, error } = await supabase
        .from('tags')
        .insert({
          name: trimmedQuery,
          organization_id: organizationId,
          color: `hsl(${Math.random() * 360}, 70%, 50%)`,
        })
        .select()
        .single();

      if (error) throw error;

      if (data) {
        // Add to temp selection
        setTempSelectedTags([...tempSelectedTags, data.id]);
        setQuery('');
        toast.success(`Tag "${trimmedQuery}" created`);
        onTagCreated?.();
      }
    } catch (error: any) {
      console.error('Error creating tag:', error);
      toast.error(error.message || 'Failed to create tag');
    } finally {
      setIsCreating(false);
    }
  };

  const tempSelectedTagObjects = options.filter(tag => 
    tempSelectedTags.includes(tag.id)
  );

  const filteredTags = options;

  const exactMatch = options.find(tag => normalize(tag.name) === normalizedQuery);
  const showCreateOption = normalizedQuery && !exactMatch;

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="h-8 w-8 relative">
          <Tag className="h-4 w-4" />
          {selectedTags.length > 0 && (
            <Badge className="absolute -top-1 -right-1 h-4 w-4 p-0 flex items-center justify-center text-[10px]">
              {selectedTags.length}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="z-50 bg-popover shadow-md w-80 p-0" align="end">
        <div className="flex flex-col">
          <div className="p-3 border-b">
            <h4 className="font-medium text-sm">Tags</h4>
            {tempSelectedTagObjects.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {tempSelectedTagObjects.map((tag) => {
                  const baseColor = tag.color || 'hsl(var(--primary))';
                  const transparentBg = baseColor.startsWith('hsl')
                    ? baseColor.replace(')', ' / 0.2)')
                    : baseColor + '33';
                  const borderColor = baseColor;
                  
                  return (
                    <div
                      key={tag.id}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium border-2"
                      style={{ 
                        backgroundColor: transparentBg,
                        borderColor: borderColor,
                        color: 'white'
                      }}
                    >
                      <span>{tag.name}</span>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setTempSelectedTags(tempSelectedTags.filter(id => id !== tag.id));
                        }}
                        className="opacity-70 hover:opacity-100 transition-fast"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          <Command shouldFilter={false}>
            <CommandInput
              placeholder="Search tags"
              className="h-9"
              value={query}
              onValueChange={setQuery}
            />
            <CommandList>
              {showCreateOption && (
                <CommandGroup>
                  <CommandItem
                    onSelect={createNewTag}
                    disabled={isCreating}
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    Create "{query.trim()}"
                  </CommandItem>
                </CommandGroup>
              )}
              <CommandEmpty>No tags found.</CommandEmpty>
              <CommandGroup>
                {filteredTags.map((tag) => (
                  <CommandItem
                    key={tag.id}
                    value={normalize(tag.name)}
                    onSelect={() => toggleTag(tag.id)}
                  >
                    <div className="flex items-center gap-2 flex-1">
                      {tag.color && (
                        <div
                          className="w-3 h-3 rounded-full shrink-0"
                          style={{ backgroundColor: tag.color }}
                        />
                      )}
                      {tag.name.trim()}
                    </div>
                    <Check
                      className={cn(
                        "ml-auto h-4 w-4",
                        tempSelectedTags.includes(tag.id) ? "opacity-100" : "opacity-0"
                      )}
                    />
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
          <div className="p-3 border-t flex gap-2">
            <Button
              variant="outline"
              size="sm"
              className="flex-1"
              onClick={() => {
                setTempSelectedTags([]);
                onTagsChange([]);
                setOpen(false);
              }}
            >
              Clear
            </Button>
            <Button
              size="sm"
              className="flex-1"
              onClick={handleApply}
            >
              Apply
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
